import {
  Input,
  Select,
  Badge,
  Button,
  Form,
  Modal,
  Spin,
  Table,
  Tag,
  Popover,
  Dropdown,
  Menu,
  Tooltip,
} from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import {
  ActionMenuIcon,
  EditAppointmentIcon,
  SendIntakeIcon,
  UserAiIcon,
} from '../../icons';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
import {
  getAllOPOrganizationAction,
  getAllOpenDROrganizationAction,
} from 'src/modules/SuperAdmin/store/actions/organization.action';
import {
  getUserInfoAction,
  resendActivationEmailAction,
  updateFronteggRoleAction,
  updatePractitionerEhrIdAction,
} from 'src/store/actions/userManagement.action';
import { getAllFronteggRoles } from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
import { useIsOpenDr, useIsOP } from 'src/hooks/misc';

const HQUserManagementPage = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isOpenDr = useIsOpenDr();
  const isOP = useIsOP();
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [tableData, setTableData] = useState([]);
  const lastSelectedOrgRef = useRef(null);
  const [fronteggRoleOptions, setFronteggRoleOptions] = useState([]);
  const [isEditRoleModalVisible, setIsEditRoleModalVisible] = useState(false);
  const [isEditFhirIdModalVisible, setIsEditFhirIdModalVisible] =
    useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'Activated', label: 'Activated' },
    { value: 'PendingInvitation', label: 'Invited' },
    { value: 'PendingLogin', label: 'Pending Login' },
  ];

  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (isOP) {
      dispatch(getAllOPOrganizationAction());
    } else if (isOpenDr) {
      dispatch(getAllOpenDROrganizationAction());
    }
  }, [dispatch, isOP, isOpenDr]);

  useEffect(() => {
    dispatch(getAllFronteggRoles());
  }, [dispatch]);

  const { hQOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );

  const { fronteggRolesAll } = useSelector((state) => state.doctorsState);

  const { userInfo, userInfoLoading } = useSelector(
    (state) => state.userManagementState
  );

  useEffect(() => {
    if (selectedOrg && selectedOrg !== lastSelectedOrgRef.current) {
      setTableData([]);
      lastSelectedOrgRef.current = selectedOrg;
      dispatch(getUserInfoAction(selectedOrg));
    }
  }, [selectedOrg, dispatch]);

  useEffect(() => {
    if (fronteggRolesAll && Array.isArray(fronteggRolesAll)) {
      let dataArr = [];
      if (isOP) {
        dataArr = fronteggRolesAll
          .filter((role) => role?.name?.startsWith('OP'))
          .map((role) => ({
            value: role.id,
            label: role.name,
          }));
      } else if (isOpenDr) {
        dataArr = fronteggRolesAll
          .filter((role) => role?.name?.startsWith('OpenDr'))
          .map((role) => ({
            value: role.id,
            label: role.name,
          }));
      } else {
        dataArr = fronteggRolesAll.map((role) => ({
          value: role.id,
          label: role.name,
        }));
      }
      setFronteggRoleOptions(dataArr);
    } else {
      setFronteggRoleOptions([]);
    }
  }, [fronteggRolesAll, isOP, isOpenDr]);

  useEffect(() => {
    if (hQOrganizationsList && Array.isArray(hQOrganizationsList)) {
      let dataArr = hQOrganizationsList
        .filter(
          (org) =>
            !orgSearchTerm ||
            org.org_name.toLowerCase().includes(orgSearchTerm.toLowerCase())
        )
        .map((key) => ({
          value: key?.org_uuid,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
          org_id: key?.org_id,
        }));
      setOrganizationOptions(dataArr);
    }
  }, [hQOrganizationsList, orgSearchTerm]);

  const handleMenuClick = (key, record) => {
    if (key === 'edit-role') {
      setEditingRecord(record);
      setIsEditRoleModalVisible(true);
    }
    if (key === 'edit-fhir-id') {
      setEditingRecord(record);
      setIsEditFhirIdModalVisible(true);
    }
    if (key === 'resend-email') {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Resend Activation Email`, {
        email: user?.email,
        hq_account: isOP ? 'OP' : 'OpenDr',
      });
      dispatch(
        resendActivationEmailAction(selectedOrg, record?.user_id, record?.email)
      );
    }
  };

  const menuOptions = (record) => (
    <Menu onClick={({ key }) => handleMenuClick(key, record)}>
      <Menu.Item key="edit-role">
        <span className="flex items-center dropdown-text space-x-2">
          <UserAiIcon /> <span>Edit role</span>
        </span>
      </Menu.Item>
      {record?.practitioner_id && (
        <Menu.Item key="edit-fhir-id">
          <span className="flex items-center dropdown-text space-x-2">
            <EditAppointmentIcon /> <span>Edit FHIR ID</span>
          </span>
        </Menu.Item>
      )}
      {record.status === 'PendingInvitation' && (
        <Menu.Item key="resend-email">
          <span className="flex items-center dropdown-text space-x-2">
            <SendIntakeIcon /> <span>Resend activation email</span>
          </span>
        </Menu.Item>
      )}
    </Menu>
  );

  // Table columns configuration
  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: 'FHIR ID',
      dataIndex: 'ehr_id',
      key: 'ehr_id',
      render: (text) => (
        <span className="text-gray-600">{text ? text : 'N/A'}</span>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles) => {
        if (!roles || roles.length === 0) {
          return null;
        }
        const tagStyle = {
          background: '#f3f4f6',
          color: '#374151',
          border: '1px solid #d1d5db',
          padding: '1px 4px',
        };
        const visibleRoles = roles.slice(0, 1);
        const hiddenRoles = roles.slice(1);
        const popoverContent = (
          <div className="flex flex-col gap-2">
            {hiddenRoles.map((role, index) => (
              <Tag key={index} style={tagStyle} className="text-xs">
                {role}
              </Tag>
            ))}
          </div>
        );
        return (
          <div className="flex flex-wrap items-center gap-1">
            {visibleRoles.map((role, index) => (
              <Tag key={index} style={tagStyle} className="text-xs">
                {role}
              </Tag>
            ))}
            {hiddenRoles.length > 0 && (
              <Popover content={popoverContent} trigger="hover" placement="top">
                <Tag style={tagStyle} className="text-xs cursor-pointer">
                  +{hiddenRoles.length}
                </Tag>
              </Popover>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'default';
        let text = status;

        if (status === 'Activated') {
          color = 'success';
          text = 'Activated';
        } else if (status === 'PendingInvitation') {
          color = 'warning';
          text = 'Invited';
        } else if (status === 'PendingLogin') {
          color = 'processing';
          text = 'Pending Login';
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (lastLogin) => {
        if (!lastLogin) {
          return <span className="text-gray-400">Never</span>;
        }

        const date = new Date(lastLogin);
        const formattedDate = date.toLocaleDateString();
        const formattedTime = date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        });

        return (
          <div className="flex flex-col">
            <span className="text-sm">{formattedDate}</span>
            <span className="text-xs text-gray-500">{formattedTime}</span>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      width: '1%',
      render: (text, record) => (
        <Dropdown
          className="actionMenu"
          overlay={menuOptions(record)}
          dropdownRender={(menu) => (
            <div className="bg-white shadow-lg rounded-lg">{menu}</div>
          )}
        >
          <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
            <ActionMenuIcon />
          </a>
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    if (selectedOrg && userInfo && Array.isArray(userInfo)) {
      let filteredData = userInfo;
      if (userSearchTerm) {
        const searchTerm = userSearchTerm.toLowerCase();
        filteredData = filteredData.filter((user) => {
          const fullName = user.full_name?.toLowerCase() || '';
          const email = user.email?.toLowerCase() || '';
          return fullName.includes(searchTerm) || email.includes(searchTerm);
        });
      }
      if (roleFilter) {
        filteredData = filteredData.filter(
          (user) => user.roles && user.roles.includes(roleFilter)
        );
      }
      if (statusFilter) {
        filteredData = filteredData.filter(
          (user) => user.status === statusFilter
        );
      }
      setTableData(
        filteredData.map((user) => ({
          ...user,
          key: user.user_id,
        }))
      );
    } else {
      setTableData([]);
    }
  }, [userInfo, selectedOrg, userSearchTerm, roleFilter, statusFilter]);

  const handleUpdateRoles = (email, newRoles, callback) => {
    dispatch(
      updateFronteggRoleAction(selectedOrg, email, newRoles, (success) => {
        callback(success);
      })
    );
  };

  const handleUpdateFhirId = (practitionerId, fhirId, callback) => {
    dispatch(
      updatePractitionerEhrIdAction(
        selectedOrg,
        practitionerId,
        fhirId,
        (success) => {
          callback(success);
        }
      )
    );
  };

  const selectedOrgName =
    organizationOptions.find((org) => org.value === selectedOrg)?.label || '';

  return (
    <div className="flex-1 pt-2">
      <div className="bg-white shadow-sm rounded-xl w-[40%] flex items-center">
        <Select
          style={{
            placeholder: {
              fontFamily: 'Poppins',
              color: '#667085',
            },
          }}
          variant="borderless"
          className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
          placeholder="Select organization"
          options={organizationOptions}
          value={selectedOrg}
          onChange={(value) => {
            setSelectedOrg(value);
            setOrgSearchTerm('');
            setUserSearchTerm('');
            setRoleFilter('');
            setStatusFilter('');
          }}
          onSearch={(value) => setOrgSearchTerm(value)}
          showSearch
          filterOption={false}
        ></Select>
      </div>
      <div className="flex gap-2 items-center w-full mt-4">
        <div className="w-[30%]">
          <Input
            placeholder="Search users by name, or email"
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
          />
        </div>
        <div className="w-[20%]">
          <Select
            allowClear
            placeholder="Filter by role"
            options={[
              { value: '', label: 'All Roles' },
              ...(isOP
                ? fronteggRoleOptions.filter((role) =>
                    role.label.startsWith('OP')
                  )
                : isOpenDr
                  ? fronteggRoleOptions.filter((role) =>
                      role.label.startsWith('OpenDr')
                    )
                  : fronteggRoleOptions
              ).map((role) => ({ value: role.label, label: role.label })),
            ]}
            value={roleFilter}
            onChange={setRoleFilter}
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
          />
        </div>
        <div className="w-[20%]">
          <Select
            allowClear
            placeholder="Filter by status"
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
          />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-1 mt-4">
        {/* User Table */}
        <div className="mt-1">
          <Table
            columns={columns}
            dataSource={tableData}
            loading={userInfoLoading}
            rowKey="user_id"
            pagination={{
              pageSize: 10,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} users`,
            }}
            locale={{
              emptyText: 'No users found for this organization',
            }}
          />
        </div>
      </div>

      {isEditRoleModalVisible && (
        <EditRoleModal
          visible={isEditRoleModalVisible}
          onCancel={() => setIsEditRoleModalVisible(false)}
          record={editingRecord}
          roles={fronteggRoleOptions}
          onUpdate={handleUpdateRoles}
          orgName={selectedOrgName}
        />
      )}
      {isEditFhirIdModalVisible && (
        <EditFhirIdModal
          visible={isEditFhirIdModalVisible}
          onCancel={() => setIsEditFhirIdModalVisible(false)}
          record={editingRecord}
          onUpdate={handleUpdateFhirId}
          orgName={selectedOrgName}
        />
      )}
    </div>
  );
};

const EditRoleModal = ({
  visible,
  onCancel,
  record,
  roles,
  onUpdate,
  orgName,
}) => {
  const [selectedRole, setSelectedRole] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const isOP = useIsOP();
  useEffect(() => {
    if (record?.roles && roles?.length) {
      const initialRoleIds = record.roles
        .map((roleName) => {
          const role = roles.find((r) => r.label === roleName);
          return role ? role.value : null;
        })
        .filter(Boolean);
      setSelectedRole(initialRoleIds);
    }
  }, [record, roles]);

  const ROLE_LABELS = {
    SUPER_ADMIN: 'OP Super Admin',
    FULL_TIME: 'OP Practice Provider Full-Time',
    PART_TIME: 'OP Practice Provider Part-Time',
    PRACTICE_ADMIN: 'OP Practice Admin',
    PRACTICE_USERS: 'OP Practice Users',
    FREE: 'OP Practice Provider Free',
  };

  const allRoleLabels = Array.isArray(roles) ? roles.map((r) => r.label) : [];
  const currentRoleLabel = Array.isArray(record?.roles)
    ? record.roles.find((r) => allRoleLabels.includes(r)) || null
    : null;

  const computePermittedRoleLabels = (
    currentRoleLabelArg,
    allRoleLabelsArg,
    isOpTenant
  ) => {
    if (!allRoleLabelsArg.length) return [];
    if (!isOpTenant) return allRoleLabelsArg;

    const allExceptSuperAdmin = allRoleLabelsArg.filter(
      (label) => label !== ROLE_LABELS.SUPER_ADMIN
    );

    switch (currentRoleLabelArg) {
      case ROLE_LABELS.SUPER_ADMIN:
        return allRoleLabelsArg;
      case ROLE_LABELS.FULL_TIME:
        return [ROLE_LABELS.FULL_TIME];
      case ROLE_LABELS.PART_TIME:
        return [ROLE_LABELS.PART_TIME, ROLE_LABELS.FULL_TIME];
      case ROLE_LABELS.PRACTICE_ADMIN:
      case ROLE_LABELS.PRACTICE_USERS:
        return allExceptSuperAdmin;
      default:
        return allExceptSuperAdmin;
    }
  };

  const permittedTargetLabels = computePermittedRoleLabels(
    currentRoleLabel,
    allRoleLabels,
    isOP
  );
  const permittedLabelsSet = new Set(permittedTargetLabels);

  const handleSaveRole = () => {
    setIsLoading(true);
    // eslint-disable-next-line no-undef
    analytics.track(`Clicked Save Role`, {
      email: user?.email,
      role: roles.find((r) => r.value === selectedRole)?.label,
      hq_account: isOP ? 'OP' : 'OpenDr',
    });
    onUpdate(record.email, [selectedRole], (success) => {
      setIsLoading(false);
      if (success) {
        onCancel();
      }
    });
  };

  return (
    <Modal
      title={<span className="font-bold text-lg">Edit role</span>}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSaveRole}
          loading={isLoading}
          disabled={Array.isArray(selectedRole)}
        >
          Save
        </Button>,
      ]}
      width={600}
      destroyOnHidden
      centered
    >
      <div className="py-4">
        <p className="text-gray-600 mb-4">
          Select role to be granted to{' '}
          <span className="font-bold">{record?.full_name}</span> while accessing
          account <span className="font-bold">{orgName}</span>
        </p>
        <Select
          style={{ width: '100%' }}
          placeholder="Select role"
          value={selectedRole}
          onChange={setSelectedRole}
          options={roles.map((role) => {
            const isDisabled =
              !permittedLabelsSet.has(role.label) &&
              !(
                (Array.isArray(selectedRole) &&
                  selectedRole.includes(role.value)) ||
                selectedRole === role.value
              );

            return {
              ...role,
              label: isDisabled ? (
                <Tooltip
                  title={
                    <span className="text-gray-600">
                      Please email{' '}
                      <a
                        className="text-blue-600 underline font-medium"
                        href="mailto:opie@insighthealth.ai"
                      >
                        opie@insighthealth.ai
                      </a>{' '}
                      for downgrades.
                    </span>
                  }
                >
                  <div className="block w-full">{role.label}</div>
                </Tooltip>
              ) : (
                role.label
              ),
              disabled: isDisabled,
            };
          })}
          optionLabelProp="label"
        />
      </div>
    </Modal>
  );
};

export default HQUserManagementPage;

const EditFhirIdModal = ({ visible, onCancel, record, onUpdate, orgName }) => {
  const [fhirId, setFhirId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const isOP = useIsOP();

  useEffect(() => {
    if (record?.ehr_id) {
      setFhirId(record.ehr_id);
    } else {
      setFhirId('');
    }
  }, [record]);

  const handleSave = () => {
    setIsLoading(true);
    // eslint-disable-next-line no-undef
    analytics.track(`Clicked Save FHIR ID`, {
      email: user?.email,
      hq_account: isOP ? 'OP' : 'OpenDr',
    });
    onUpdate(record?.practitioner_id, fhirId, (success) => {
      setIsLoading(false);
      if (success) {
        onCancel();
      }
    });
  };

  return (
    <Modal
      title={<span className="font-bold text-lg">Edit FHIR ID</span>}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={!fhirId || fhirId === record?.ehr_id}
        >
          Save
        </Button>,
      ]}
      width={600}
      destroyOnHidden
      centered
    >
      <div className="py-4">
        <p className="text-gray-600 mb-4">
          Enter FHIR ID for{' '}
          <span className="font-bold">{record?.full_name}</span> in account{' '}
          <span className="font-bold">{orgName}</span>
        </p>
        <Input
          placeholder="Enter FHIR ID"
          value={fhirId}
          onChange={(e) => setFhirId(e.target.value)}
        />
      </div>
    </Modal>
  );
};
